/**
 * MCP Tool Registry
 * Manages registration and execution of CRM tools
 */

import { MCPTool, MCPExecutionContext, MCPToolMetadata } from '../types';

interface RegisteredTool {
  tool: MCPTool;
  metadata: MCPToolMetadata;
  registeredAt: Date;
}

export class MCPToolRegistry {
  private tools: Map<string, RegisteredTool> = new Map();
  private initialized: boolean = false;

  async initialize(): Promise<void> {
    if (this.initialized) {
      return;
    }

    console.log('🔧 Initializing MCP Tool Registry...');
    
    // Tools will be registered dynamically by importing tool modules
    // This happens in the tool modules themselves
    
    this.initialized = true;
    console.log(`✅ Tool Registry initialized with ${this.tools.size} tools`);
  }

  /**
   * Register a tool
   */
  registerTool(tool: MCPTool, metadata?: Partial<MCPToolMetadata>): void {
    if (this.tools.has(tool.name)) {
      console.warn(`⚠️  Tool ${tool.name} is already registered, overwriting...`);
    }

    const fullMetadata: MCPToolMetadata = {
      category: 'search',
      requiresAuth: true,
      dangerLevel: 'safe',
      ...metadata
    };

    this.tools.set(tool.name, {
      tool,
      metadata: fullMetadata,
      registeredAt: new Date()
    });

    console.log(`✅ Registered tool: ${tool.name} (${fullMetadata.category})`);
  }

  /**
   * Get all tools (filtered by org permissions and paginated)
   */
  async listTools(context: MCPExecutionContext, cursor?: string, limit?: number): Promise<{ tools: MCPTool[], nextCursor?: string }> {
    const allowedTools = context.orgConfig.mcp.allowedTools;
    const allTools = Array.from(this.tools.values());

    // Filter by allowed tools
    let filteredTools = allTools;
    if (allowedTools.length > 0 && !allowedTools.includes('*')) {
      filteredTools = allTools.filter(rt => allowedTools.includes(rt.tool.name));
    }

    // Pagination
    const pageSize = limit || filteredTools.length;
    const startIndex = cursor ? parseInt(cursor, 10) : 0;
    const paginatedTools = filteredTools.slice(startIndex, startIndex + pageSize);
    
    const nextCursor = (startIndex + pageSize < filteredTools.length) 
      ? (startIndex + pageSize).toString() 
      : undefined;

    return {
      tools: paginatedTools.map(rt => rt.tool),
      nextCursor
    };
  }

  /**
   * Execute a tool
   */
  async executeTool(
    name: string,
    params: any,
    context: MCPExecutionContext
  ): Promise<any> {
    const registered = this.tools.get(name);
    
    if (!registered) {
      throw new Error(`Tool not found: ${name}`);
    }

    // Check if tool is allowed for this org
    const allowedTools = context.orgConfig.mcp.allowedTools;
    if (
      allowedTools.length > 0 && 
      !allowedTools.includes('*') && 
      !allowedTools.includes(name)
    ) {
      throw new Error(`Tool ${name} is not allowed for this organization`);
    }

    // Validate parameters against schema
    this.validateParams(params, registered.tool.inputSchema);

    // Execute the tool
    const startTime = Date.now();
    try {
      const result = await registered.tool.execute(params, context);
      const duration = Date.now() - startTime;
      
      console.log(`✅ Tool executed: ${name} in ${duration}ms`);
      
      return result;
    } catch (error: any) {
      const duration = Date.now() - startTime;
      console.error(`❌ Tool execution failed: ${name} after ${duration}ms`, error);
      throw error;
    }
  }

  /**
   * Get a single tool
   */
  getTool(name: string): MCPTool | null {
    const registered = this.tools.get(name);
    return registered ? registered.tool : null;
  }

  /**
   * Get tool metadata
   */
  getToolMetadata(name: string): MCPToolMetadata | null {
    const registered = this.tools.get(name);
    return registered ? registered.metadata : null;
  }

  /**
   * Get tool count
   */
  getToolCount(): number {
    return this.tools.size;
  }

  /**
   * List all tool names
   */
  getToolNames(): string[] {
    return Array.from(this.tools.keys());
  }

  /**
   * Get tools by category
   */
  getToolsByCategory(category: string): MCPTool[] {
    return Array.from(this.tools.values())
      .filter(rt => rt.metadata.category === category)
      .map(rt => rt.tool);
  }

  /**
   * Validate parameters against schema
   */
  private validateParams(params: any, schema: any): void {
    // Basic validation - check required fields
    if (schema.required) {
      for (const field of schema.required) {
        if (params[field] === undefined) {
          throw new Error(`Missing required parameter: ${field}`);
        }
      }
    }

    // Type validation
    for (const [key, prop] of Object.entries(schema.properties || {})) {
      const value = params[key];
      
      // Skip validation if value is undefined, null, or empty string
      if (value === undefined || value === null || value === '') continue;

      const propDef = prop as any;
      const expectedType = propDef.type;
      const actualType = typeof value;

      if (expectedType === 'string' && actualType !== 'string') {
        throw new Error(`Parameter ${key} must be a string`);
      }
      if (expectedType === 'number' && actualType !== 'number') {
        throw new Error(`Parameter ${key} must be a number`);
      }
      if (expectedType === 'boolean' && actualType !== 'boolean') {
        throw new Error(`Parameter ${key} must be a boolean`);
      }
      if (expectedType === 'array' && !Array.isArray(value)) {
        throw new Error(`Parameter ${key} must be an array`);
      }
      if (expectedType === 'object' && (actualType !== 'object' || Array.isArray(value))) {
        throw new Error(`Parameter ${key} must be an object`);
      }

      // Enum validation (only if value is not empty)
      if (propDef.enum && value && !propDef.enum.includes(value)) {
        throw new Error(`Parameter ${key} must be one of: ${propDef.enum.join(', ')}`);
      }
    }
  }

  /**
   * Unregister a tool
   */
  unregisterTool(name: string): boolean {
    return this.tools.delete(name);
  }

  /**
   * Clear all tools
   */
  clear(): void {
    this.tools.clear();
  }
}

